import { SWIPER } from '../constants';

const GALLERY_SELECTOR = '[data-variant-gallery]';
const POOL_SELECTOR = '[data-gallery-slide-pool]';
const WRAPPER_SELECTOR = '.swiper-wrapper';

// Every slide a gallery can show, captured once per gallery element. The
// server renders the selected variant's media into the swiper and parks the
// other variants' media in a <template> pool (see product-gallery.liquid).
const slidePools = new WeakMap();

// Set by product-gallery-slide.liquid: filename sequence for SKU-named files,
// otherwise gallery position (offset). The lead slide has none and sorts first.
const getMediaOrder = (slideEl) => Number(slideEl.dataset.mediaOrder) || 0;

// Slides in logical order. Swiper's loop mode rotates slides in the DOM but
// stamps each with its original index, so sort by that when present.
const getRenderedSlides = (wrapperEl) => {
  const slideEls = [...wrapperEl.querySelectorAll(`:scope > ${SWIPER.SLIDE_CLASS}`)];
  const hasLoopIndex = slideEls.every((slideEl) => 'swiperSlideIndex' in slideEl.dataset);
  if (!hasLoopIndex) return slideEls;
  return slideEls.sort((slideA, slideB) => Number(slideA.dataset.swiperSlideIndex) - Number(slideB.dataset.swiperSlideIndex));
};

const isSameOrder = (slideElsA, slideElsB) => slideElsA.length === slideElsB.length
  && slideElsA.every((slideEl, index) => slideEl === slideElsB[index]);

// Tags are variant ids (native admin attachment) and SKUs (alt-text tag)
const getMediaTags = (slideEl) => (slideEl.dataset.mediaTags || '').split(',').filter(Boolean);

const isTaggedForVariant = (slideEl, variant) => {
  const tags = getMediaTags(slideEl);
  if (tags.includes(String(variant.id))) return true;
  return Boolean(variant.sku) && tags.includes(variant.sku);
};

const getSlidePool = (galleryEl) => {
  if (slidePools.has(galleryEl)) return slidePools.get(galleryEl);

  const wrapperEl = galleryEl.querySelector(WRAPPER_SELECTOR);
  const poolEl = galleryEl.querySelector(POOL_SELECTOR);
  if (!wrapperEl) return [];

  const slideEls = getRenderedSlides(wrapperEl);
  if (poolEl) slideEls.push(...poolEl.content.querySelectorAll(SWIPER.SLIDE_CLASS));
  slideEls.sort((slideA, slideB) => getMediaOrder(slideA) - getMediaOrder(slideB));

  slidePools.set(galleryEl, slideEls);
  poolEl?.remove();
  return slideEls;
};

// Pinned lead media (detailed image), then this variant's media, then shared
// media. A variant with nothing tagged or shared shows everything rather than
// an empty gallery.
const filterSlides = (slideEls, variant, limit) => {
  const leadEls = slideEls.filter((slideEl) => 'galleryLead' in slideEl.dataset);
  const variantEls = slideEls.filter((slideEl) => isTaggedForVariant(slideEl, variant));
  const sharedEls = slideEls.filter((slideEl) => !getMediaTags(slideEl).length && !leadEls.includes(slideEl));
  const filteredEls = [...leadEls, ...variantEls, ...sharedEls];
  const resultEls = filteredEls.length ? filteredEls : slideEls;
  return limit ? resultEls.slice(0, limit) : resultEls;
};

// Same steps Swiper's Manipulation module runs for appendSlide, done in a
// single pass so loop mode never sees an empty (warning-triggering) wrapper.
// Before Swiper has initialised (e.g. the ATC popup), the DOM swap is enough.
// Swiper defers slideToLoop to the next frame, so callers that want a
// different slide pass activeIndex rather than sliding afterwards.
const renderSlides = (galleryEl, slideEls, activeIndex = 0) => {
  const wrapperEl = galleryEl.querySelector(WRAPPER_SELECTOR);
  if (!wrapperEl) return;

  const { swiper } = galleryEl;
  if (!swiper) {
    wrapperEl.replaceChildren(...slideEls);
    return;
  }

  swiper.loopDestroy();
  wrapperEl.replaceChildren(...slideEls);
  swiper.recalcSlides();
  swiper.loopCreate();
  swiper.update();
  // A11y only re-labels slides ("n / N") on length-change events, which a
  // same-count rebuild never fires
  swiper.emit('slidesLengthChange');
  swiper.slideToLoop(Math.min(activeIndex, slideEls.length - 1), 0);
};

// `limit` caps the rendered set (product card carousel shows the first N);
// `activeIndex` is the slide to show after a rebuild, clamped to the new set
const updateProductGallery = (variant, containerEl = document, { limit, activeIndex } = {}) => {
  if (!variant) return;
  const galleryEls = containerEl.querySelectorAll(GALLERY_SELECTOR);
  if (!galleryEls.length) return;

  galleryEls.forEach((galleryEl) => {
    const slideEls = getSlidePool(galleryEl);
    if (!slideEls.length) return;

    const filteredEls = filterSlides(slideEls, variant, limit);
    // Server-rendered order already matches (initial page load) — nothing to do
    const wrapperEl = galleryEl.querySelector(WRAPPER_SELECTOR);
    if (wrapperEl && isSameOrder(getRenderedSlides(wrapperEl), filteredEls)) return;
    renderSlides(galleryEl, filteredEls, activeIndex);
  });
};

export default updateProductGallery;
